import i18next from 'i18next';
import ICU from 'i18next-icu';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { resolveLang } from '@/config';

const version = process.env.GIT_SHA || Date.now();

const setHtmlLang = (lng: string) => {
  document.documentElement.lang = lng;
};

// Toggle "cimode" — i18next built-in that makes t() return the key itself
// instead of the translation. Useful for finding the Tolgee key behind a
// rendered string. Usage:
//   ?i18n=keys  → switch on (persists via localStorage)
//   ?i18n=off   → switch back to detected language
// Or from the console (dev only): window.i18n.changeLanguage('cimode')
const I18N_QUERY_PARAM = 'i18n';
const I18N_STORAGE_KEY = 'i18nextLng';
const params = new URLSearchParams(window.location.search);
const i18nParam = params.get(I18N_QUERY_PARAM);
if (i18nParam === 'keys') {
  localStorage.setItem(I18N_STORAGE_KEY, 'cimode');
} else if (i18nParam === 'off') {
  localStorage.removeItem(I18N_STORAGE_KEY);
}

void i18next
  .use(ICU)
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: import.meta.env.DEV,

    // IMPORTANT: don't collapse zh-Hant-TW to zh
    load: 'currentOnly',

    supportedLngs: [
      'en',
      'cs',
      'de',
      'es',
      'fr',
      'it',
      'sk',
      'zh-Hant-TW',
      'cimode',
    ],
    fallbackLng: 'en',
    appendNamespaceToCIMode: true,

    interpolation: { escapeValue: false },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],

      // IMPORTANT: map "ZH" -> "zh-Hant-TW" *before* i18next validates supportedLngs
      convertDetectedLanguage: (lng: string) => resolveLang(lng),
    },

    backend: {
      loadPath: (lngs: string[], ns: string) => {
        const lng = resolveLang(lngs?.[0]);
        return `/locales/${lng}/${ns}.json?v=${version}`;
      },
    },
  })
  .then(() => {
    // Beim initialen Laden die erkannte Sprache setzen
    setHtmlLang(i18next.language);
  });

i18next.on('languageChanged', setHtmlLang);

if (import.meta.env.DEV) {
  (window as unknown as { i18n: typeof i18next }).i18n = i18next;
}

export default i18next;
